import { DIFFICULTIES } from './ai/difficulty';
import type { Difficulty } from './ai/types';
import type { GameSettings } from './mode';
import { movablePieces, setupBlackOptions, setupWhiteOptions } from './rules';
import type { Transition } from './state';
import { cap, instructionLabel, turnLabel } from './strings';
import { BLACK, type Color, type GameState, N, PHASE, type Position, WHITE } from './types';

export interface UIHandlers {
  onCellClick: (r: number, c: number) => void;
  onNewGame: () => void;
  onUndo: () => void;
  onEndTurn: () => void;
  onToggleRules: () => void;
  onPlayAgain: () => void;
  /** Submit mode-dialog selections. Caller resets the game. */
  onModeStart: (settings: GameSettings) => void;
  /** Dismiss the mode dialog without changing anything. */
  onModeCancel: () => void;
}

export interface UI {
  root: HTMLElement;
  turnLine: HTMLElement;
  instrLine: HTMLElement;
  modeLine: HTMLElement;
  sboxBlack: HTMLElement;
  sboxWhite: HTMLElement;
  capBlack: HTMLElement;
  capWhite: HTMLElement;
  boardEl: HTMLElement;
  cellEls: HTMLElement[][];
  btnEnd: HTMLButtonElement;
  btnUndo: HTMLButtonElement;
  btnNew: HTMLButtonElement;
  btnRules: HTMLButtonElement;
  rulesPanel: HTMLElement;
  modalOverlay: HTMLElement;
  modalStone: HTMLElement;
  modalTitle: HTMLElement;
  modalDesc: HTMLElement;
  modeOverlay: HTMLElement;
  modeWarning: HTMLElement;
  modeStart: HTMLButtonElement;
  modeCancel: HTMLButtonElement;
  modeFieldsetAI: HTMLFieldSetElement;
  thinking: HTMLElement;
  handlers: UIHandlers;
  focused: Position;
}

export interface RenderOpts {
  canUndo: boolean;
  silentScores?: boolean;
  /** Current mode + human color (for the mode line + label augmentation). */
  mode: GameSettings['mode'];
  difficulty: Difficulty;
  humanColor: Color | null;
  /** True while AI is computing or replaying its move; disables board input visually. */
  aiBusy: boolean;
  /** If set, a player-visible error replaces the mode line (styled as error). */
  aiError?: string | null;
}

export function buildUI(root: HTMLElement, handlers: UIHandlers): UI {
  const difficultyOptions = DIFFICULTIES.map(
    (d) =>
      `<label class="radio">
        <input type="radio" name="md-difficulty" value="${d.level}" ${d.level === 3 ? 'checked' : ''}>
        <span class="radio-label">
          <span class="radio-title">${d.name}</span>
          <span class="radio-desc">${d.description}</span>
        </span>
      </label>`,
  ).join('');

  root.innerHTML = `
    <a class="skip-link" href="#board">Skip to board</a>
    <header>
      <h1>Kōnane</h1>
      <p>Ancient Hawaiian Strategy Game</p>
    </header>
    <div id="status" aria-live="polite" aria-atomic="true">
      <div id="turn-line"></div>
      <div id="instr-line"></div>
      <div id="mode-line"></div>
    </div>
    <div id="scores" role="status" aria-label="Captured stones">
      <div class="sbox" id="sbox-black">
        <div class="sbox-label">
          <span class="dot dot-black" aria-hidden="true"></span><span class="sbox-name" id="sbox-black-name">Black captured</span>
        </div>
        <div class="sbox-num" id="cap-black">0</div>
      </div>
      <div class="sbox" id="sbox-white">
        <div class="sbox-label">
          <span class="dot dot-white" aria-hidden="true"></span><span class="sbox-name" id="sbox-white-name">White captured</span>
        </div>
        <div class="sbox-num" id="cap-white">0</div>
      </div>
    </div>
    <div id="board-outer">
      <div class="col-row" id="col-labels" aria-hidden="true"></div>
      <div class="board-wrap">
        <div class="row-col" id="row-labels" aria-hidden="true"></div>
        <div id="board" role="grid" aria-label="Kōnane board, 8 by 8" tabindex="-1"></div>
        <div id="thinking" class="thinking" hidden aria-live="polite">
          <div class="thinking-spinner" aria-hidden="true"></div>
          <span class="thinking-text">Computer is thinking…</span>
        </div>
      </div>
    </div>
    <div id="controls">
      <button id="btn-end" type="button" hidden>End Turn</button>
      <button id="btn-undo" type="button" disabled>Undo</button>
      <button id="btn-new" type="button">New Game</button>
      <button id="btn-rules" type="button" aria-expanded="false" aria-controls="rules-panel">Rules</button>
    </div>
    <section id="rules-panel" hidden aria-label="Rules of Kōnane">
      <h3>How to Play</h3>
      <p><strong>The board</strong> starts with 64 stones — black and white alternating like a checkerboard.</p>
      <h3>Setup</h3>
      <p><strong>Black goes first</strong> by removing one of their own stones from either a <em>corner</em> or one of the four <em>center</em> squares. Only two corners and two center squares hold black stones; the game highlights the valid choices.</p>
      <p><strong>White then removes</strong> one of their own stones from a square directly next to where Black just removed (up, down, left, or right — not diagonal).</p>
      <h3>Moving</h3>
      <p>From this point on, <strong>every move is a jump</strong>. Pick one of your stones, then jump <em>orthogonally</em> over an opponent stone right next to it, landing on the empty square just beyond. The opponent stone is captured and removed.</p>
      <p>You can only jump in one of the four straight directions (up, down, left, right) — never diagonally.</p>
      <h3>Multi-capture</h3>
      <p>After a jump, if the <em>same stone</em> can continue jumping in the <em>same direction</em>, you may keep going. You cannot turn during a multi-capture, but you can stop at any time by pressing <strong>End Turn</strong>.</p>
      <h3>Winning</h3>
      <p>The first player who <strong>cannot make a legal jump</strong> on their turn loses. The total number of stones captured does not matter — only whether you have a legal move.</p>
      <h3>Tips</h3>
      <p>Stones that have a legal jump glow gold. Tap one to see where it can land. The teal dots show first jumps; orange dots show continuations in the locked direction.</p>
    </section>
    <div id="modal-overlay" class="overlay" hidden role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <div class="dialog">
        <div id="modal-stone" aria-hidden="true"></div>
        <h2 id="modal-title">Black Wins!</h2>
        <p id="modal-desc">White has no jumps remaining.</p>
        <div class="dialog-buttons">
          <button id="btn-again" class="dialog-primary" type="button">Play Again</button>
          <button id="btn-modal-close" class="dialog-secondary" type="button">Close</button>
        </div>
      </div>
    </div>
    <div id="mode-overlay" class="overlay" hidden role="dialog" aria-modal="true" aria-labelledby="mode-title">
      <div class="dialog dialog-wide">
        <h2 id="mode-title">New Game</h2>
        <p id="mode-warning" hidden>Starting a new game will discard your current game.</p>
        <form id="mode-form">
          <fieldset class="mode-fieldset">
            <legend>Players</legend>
            <label class="radio">
              <input type="radio" name="md-mode" value="two_player" checked>
              <span class="radio-label">
                <span class="radio-title">Two players</span>
                <span class="radio-desc">Take turns on the same device.</span>
              </span>
            </label>
            <label class="radio">
              <input type="radio" name="md-mode" value="vs_computer">
              <span class="radio-label">
                <span class="radio-title">One player vs computer</span>
                <span class="radio-desc">The computer takes the other side.</span>
              </span>
            </label>
          </fieldset>
          <fieldset class="mode-fieldset mode-ai" id="mode-ai-fields" disabled>
            <legend>Difficulty</legend>
            ${difficultyOptions}
            <legend class="mode-color-legend">You play</legend>
            <label class="radio">
              <input type="radio" name="md-color" value="black" checked>
              <span class="radio-label">
                <span class="radio-title">Black <span class="muted">(go first)</span></span>
              </span>
            </label>
            <label class="radio">
              <input type="radio" name="md-color" value="white">
              <span class="radio-label">
                <span class="radio-title">White <span class="muted">(go second)</span></span>
              </span>
            </label>
            <label class="radio">
              <input type="radio" name="md-color" value="random">
              <span class="radio-label">
                <span class="radio-title">Random</span>
              </span>
            </label>
          </fieldset>
        </form>
        <div class="dialog-buttons">
          <button id="btn-mode-start" class="dialog-primary" type="button">Start Game</button>
          <button id="btn-mode-cancel" class="dialog-secondary" type="button">Cancel</button>
        </div>
      </div>
    </div>
  `;

  const $ = <T extends HTMLElement = HTMLElement>(id: string): T => {
    const el = root.querySelector(`#${id}`);
    if (!el) throw new Error(`Missing element #${id}`);
    return el as T;
  };

  const ui: UI = {
    root,
    turnLine: $('turn-line'),
    instrLine: $('instr-line'),
    modeLine: $('mode-line'),
    sboxBlack: $('sbox-black'),
    sboxWhite: $('sbox-white'),
    capBlack: $('cap-black'),
    capWhite: $('cap-white'),
    boardEl: $('board'),
    cellEls: [],
    btnEnd: $<HTMLButtonElement>('btn-end'),
    btnUndo: $<HTMLButtonElement>('btn-undo'),
    btnNew: $<HTMLButtonElement>('btn-new'),
    btnRules: $<HTMLButtonElement>('btn-rules'),
    rulesPanel: $('rules-panel'),
    modalOverlay: $('modal-overlay'),
    modalStone: $('modal-stone'),
    modalTitle: $('modal-title'),
    modalDesc: $('modal-desc'),
    modeOverlay: $('mode-overlay'),
    modeWarning: $('mode-warning'),
    modeStart: $<HTMLButtonElement>('btn-mode-start'),
    modeCancel: $<HTMLButtonElement>('btn-mode-cancel'),
    modeFieldsetAI: $<HTMLFieldSetElement>('mode-ai-fields'),
    thinking: $('thinking'),
    handlers,
    focused: { r: 0, c: 0 },
  };

  buildLabels(root);
  buildBoard(ui);

  ui.btnEnd.addEventListener('click', () => handlers.onEndTurn());
  ui.btnUndo.addEventListener('click', () => handlers.onUndo());
  ui.btnNew.addEventListener('click', () => handlers.onNewGame());
  ui.btnRules.addEventListener('click', () => {
    handlers.onToggleRules();
    const open = !ui.rulesPanel.hasAttribute('hidden');
    ui.btnRules.setAttribute('aria-expanded', String(open));
  });
  $<HTMLButtonElement>('btn-again').addEventListener('click', () => handlers.onPlayAgain());
  $<HTMLButtonElement>('btn-modal-close').addEventListener('click', () => {
    ui.modalOverlay.setAttribute('hidden', '');
  });
  ui.modeStart.addEventListener('click', () => {
    const settings = readModeForm(ui);
    handlers.onModeStart(settings);
  });
  ui.modeCancel.addEventListener('click', () => handlers.onModeCancel());

  // Mode/AI radio handling: enable/disable the AI fieldset when mode changes.
  root.addEventListener('change', (ev) => {
    const t = ev.target as HTMLInputElement | null;
    if (!t || t.name !== 'md-mode') return;
    ui.modeFieldsetAI.disabled = t.value !== 'vs_computer';
  });

  // Global Esc handler
  document.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Escape') return;
    if (!ui.modeOverlay.hasAttribute('hidden')) {
      if (!ui.modeCancel.hasAttribute('hidden')) {
        ev.preventDefault();
        handlers.onModeCancel();
      }
    } else if (!ui.modalOverlay.hasAttribute('hidden')) {
      ev.preventDefault();
      ui.modalOverlay.setAttribute('hidden', '');
    } else if (!ui.rulesPanel.hasAttribute('hidden')) {
      ev.preventDefault();
      ui.rulesPanel.setAttribute('hidden', '');
      ui.btnRules.setAttribute('aria-expanded', 'false');
    }
  });

  return ui;
}

function readModeForm(ui: UI): GameSettings {
  const root = ui.root;
  const mode =
    (root.querySelector<HTMLInputElement>('input[name="md-mode"]:checked')?.value ??
      'two_player') === 'vs_computer'
      ? 'vs_computer'
      : 'two_player';
  const difficulty = clampDifficulty(
    Number(root.querySelector<HTMLInputElement>('input[name="md-difficulty"]:checked')?.value ?? 3),
  );
  const colorRaw = root.querySelector<HTMLInputElement>('input[name="md-color"]:checked')?.value;
  const humanColor: GameSettings['humanColor'] =
    colorRaw === 'white' ? WHITE : colorRaw === 'random' ? 'random' : BLACK;
  return { mode, difficulty, humanColor };
}

function clampDifficulty(n: number): Difficulty {
  const r = Math.round(n);
  if (r <= 1) return 1;
  if (r >= 5) return 5;
  return r as Difficulty;
}

export function showModeDialog(ui: UI, settings: GameSettings, allowCancel: boolean): void {
  ui.root.querySelectorAll<HTMLInputElement>('input[name="md-mode"]').forEach((input) => {
    input.checked = input.value === settings.mode;
  });
  ui.modeFieldsetAI.disabled = settings.mode !== 'vs_computer';
  ui.root.querySelectorAll<HTMLInputElement>('input[name="md-difficulty"]').forEach((input) => {
    input.checked = Number(input.value) === settings.difficulty;
  });
  ui.root.querySelectorAll<HTMLInputElement>('input[name="md-color"]').forEach((input) => {
    input.checked = input.value === settings.humanColor;
  });
  if (allowCancel) {
    ui.modeCancel.removeAttribute('hidden');
    ui.modeWarning.removeAttribute('hidden');
  } else {
    ui.modeCancel.setAttribute('hidden', '');
    ui.modeWarning.setAttribute('hidden', '');
  }
  ui.modeOverlay.removeAttribute('hidden');
  ui.modeStart.focus();
}

export function hideModeDialog(ui: UI): void {
  ui.modeOverlay.setAttribute('hidden', '');
}

function buildLabels(root: HTMLElement): void {
  const cols = root.querySelector('#col-labels');
  const rows = root.querySelector('#row-labels');
  if (!cols || !rows) return;
  for (let c = 0; c < N; c++) {
    const d = document.createElement('div');
    d.className = 'clabel';
    d.textContent = String.fromCharCode(65 + c);
    cols.appendChild(d);
  }
  for (let r = 0; r < N; r++) {
    const d = document.createElement('div');
    d.className = 'rlabel';
    d.textContent = String(N - r);
    rows.appendChild(d);
  }
}

function buildBoard(ui: UI): void {
  ui.boardEl.innerHTML = '';
  for (let r = 0; r < N; r++) {
    ui.cellEls[r] = [];
    for (let c = 0; c < N; c++) {
      const cell = document.createElement('div');
      cell.className = `cell ${(r + c) % 2 === 0 ? 'lt' : 'dk'}`;
      cell.setAttribute('role', 'gridcell');
      cell.dataset.row = String(r);
      cell.dataset.col = String(c);
      cell.tabIndex = r === 0 && c === 0 ? 0 : -1;
      cell.addEventListener('click', () => ui.handlers.onCellClick(r, c));
      cell.addEventListener('keydown', (ev) => onCellKey(ui, ev, r, c));
      ui.boardEl.appendChild(cell);
      ui.cellEls[r][c] = cell;
    }
  }
}

function onCellKey(ui: UI, ev: KeyboardEvent, r: number, c: number): void {
  let nr = r;
  let nc = c;
  switch (ev.key) {
    case 'ArrowUp':
      nr = Math.max(0, r - 1);
      break;
    case 'ArrowDown':
      nr = Math.min(N - 1, r + 1);
      break;
    case 'ArrowLeft':
      nc = Math.max(0, c - 1);
      break;
    case 'ArrowRight':
      nc = Math.min(N - 1, c + 1);
      break;
    case 'Home':
      nc = 0;
      break;
    case 'End':
      nc = N - 1;
      break;
    case 'PageUp':
      nr = 0;
      break;
    case 'PageDown':
      nr = N - 1;
      break;
    case 'Enter':
    case ' ':
      ev.preventDefault();
      ui.handlers.onCellClick(r, c);
      return;
    default:
      return;
  }
  ev.preventDefault();
  moveFocus(ui, nr, nc);
}

export function moveFocus(ui: UI, r: number, c: number): void {
  const prev = ui.cellEls[ui.focused.r][ui.focused.c];
  prev.tabIndex = -1;
  const next = ui.cellEls[r][c];
  next.tabIndex = 0;
  next.focus();
  ui.focused = { r, c };
}

export function render(ui: UI, state: GameState, opts: RenderOpts): void {
  renderStatus(ui, state, opts);
  renderScores(ui, state, opts.silentScores ?? false, opts.humanColor);
  renderBoard(ui, state, opts);
  renderControls(ui, state, opts.canUndo);
  renderModal(ui, state);
}

function renderStatus(ui: UI, s: GameState, opts: RenderOpts): void {
  ui.turnLine.textContent = turnLabel(s);
  ui.instrLine.textContent = opts.aiBusy ? '' : instructionLabel(s);
  if (opts.aiError) {
    ui.modeLine.textContent = opts.aiError;
    ui.modeLine.classList.add('error');
  } else {
    ui.modeLine.textContent = modeLineText(opts);
    ui.modeLine.classList.remove('error');
  }

  const active = s.phase === PHASE.OVER ? null : s.turn;
  ui.sboxBlack.classList.toggle('active', active === BLACK);
  ui.sboxWhite.classList.toggle('active', active === WHITE);
}

function modeLineText(opts: RenderOpts): string {
  if (opts.mode === 'two_player') return 'Two players';
  const diff = DIFFICULTIES.find((d) => d.level === opts.difficulty);
  const diffName = diff ? diff.name : '';
  const youAre =
    opts.humanColor === BLACK
      ? 'you play Black'
      : opts.humanColor === WHITE
        ? 'you play White'
        : '';
  return `Vs computer · ${diffName}${youAre ? ` · ${youAre}` : ''}`;
}

let prevCapBlack = 0;
let prevCapWhite = 0;

function renderScores(ui: UI, s: GameState, silent: boolean, humanColor: Color | null): void {
  const bumpBlack = !silent && s.caps.black > prevCapBlack;
  const bumpWhite = !silent && s.caps.white > prevCapWhite;
  ui.capBlack.textContent = String(s.caps.black);
  ui.capWhite.textContent = String(s.caps.white);
  if (bumpBlack) bumpScore(ui.capBlack);
  if (bumpWhite) bumpScore(ui.capWhite);
  prevCapBlack = s.caps.black;
  prevCapWhite = s.caps.white;

  // Augment score-box labels in 1-player mode to make sides obvious.
  const blackName = ui.root.querySelector<HTMLElement>('#sbox-black-name');
  const whiteName = ui.root.querySelector<HTMLElement>('#sbox-white-name');
  if (blackName && whiteName) {
    if (humanColor === null) {
      blackName.textContent = 'Black captured';
      whiteName.textContent = 'White captured';
    } else {
      blackName.textContent = humanColor === BLACK ? 'You (Black)' : 'Computer (Black)';
      whiteName.textContent = humanColor === WHITE ? 'You (White)' : 'Computer (White)';
    }
  }
}

export function resetScoreTracking(s: GameState): void {
  prevCapBlack = s.caps.black;
  prevCapWhite = s.caps.white;
}

function bumpScore(el: HTMLElement): void {
  el.classList.remove('bump');
  void el.offsetWidth;
  el.classList.add('bump');
}

function renderBoard(ui: UI, s: GameState, opts: RenderOpts): void {
  const destSet = new Set<string>(s.moves.map((m) => `${m.tr},${m.tc}`));
  const isMulti = s.phase === PHASE.MULTI;

  const setupSet = new Set<string>();
  if (s.phase === PHASE.SETUP_B) {
    for (const o of setupBlackOptions(s.board)) {
      setupSet.add(`${o.r},${o.c}`);
    }
  } else if (s.phase === PHASE.SETUP_W && s.removed) {
    for (const o of setupWhiteOptions(s.board, s.removed.r, s.removed.c)) {
      setupSet.add(`${o.r},${o.c}`);
    }
  }

  const canPickSet = new Set<string>();
  if (s.phase === PHASE.SELECT) {
    for (const o of movablePieces(s.board, s.turn)) {
      canPickSet.add(`${o.r},${o.c}`);
    }
  }

  // While the AI is busy, suppress player-side interactivity so the board
  // doesn't invite clicks that will be ignored.
  const aiBusy = opts.aiBusy;

  for (let r = 0; r < N; r++) {
    for (let c = 0; c < N; c++) {
      const key = `${r},${c}`;
      const cell = ui.cellEls[r][c];
      const isSetup = !aiBusy && setupSet.has(key);
      const isDest = !aiBusy && destSet.has(key);
      const isSel = s.sel?.r === r && s.sel?.c === c;
      const canPick = !aiBusy && canPickSet.has(key);
      const clickable = isSetup || isDest || canPick || isSel;

      setClass(cell, 'hl-setup', isSetup);
      setClass(cell, 'hl-move', isDest && !isMulti);
      setClass(cell, 'hl-multi', isDest && isMulti);
      setClass(cell, 'can-pick', canPick || isSetup);
      setClass(cell, 'clickable', clickable);

      const ariaLabel = describeCell(s, r, c, { clickable, isSel });
      cell.setAttribute('aria-label', ariaLabel);
      if (isSel) cell.setAttribute('aria-pressed', 'true');
      else cell.removeAttribute('aria-pressed');

      updateCellPiece(cell, s.board[r][c], isSel);
    }
  }
}

function setClass(el: HTMLElement, cls: string, on: boolean): void {
  el.classList.toggle(cls, on);
}

function updateCellPiece(
  cell: HTMLElement,
  content: 'black' | 'white' | null,
  isSel: boolean,
): void {
  const existing = cell.querySelector<HTMLElement>('.piece');
  if (content === null) {
    if (existing) existing.remove();
    return;
  }
  if (existing) {
    const wantClass = `piece p-${content}${isSel ? ' piece-sel' : ''}`;
    const currentBase = existing.className
      .replace(/\b(arriving|moving|capturing)\b/g, '')
      .trim()
      .replace(/\s+/g, ' ');
    if (currentBase !== wantClass) {
      const transient = ['arriving', 'moving', 'capturing'].filter((n) =>
        existing.classList.contains(n),
      );
      existing.className = transient.length ? `${wantClass} ${transient.join(' ')}` : wantClass;
    }
    if (!existing.classList.contains('moving')) {
      existing.style.transform = '';
      existing.style.zIndex = '';
      existing.style.transition = '';
    }
    return;
  }
  const piece = document.createElement('div');
  piece.className = `piece p-${content}${isSel ? ' piece-sel' : ''}`;
  cell.appendChild(piece);
}

function describeCell(
  s: GameState,
  r: number,
  c: number,
  flags: { clickable: boolean; isSel: boolean },
): string {
  const file = String.fromCharCode(65 + c);
  const rank = N - r;
  const coord = `${file}${rank}`;
  const cell = s.board[r][c];
  const piece = cell ? `${cap(cell)} stone` : 'empty';
  let suffix = '';
  if (flags.isSel) suffix = ' (selected)';
  else if (flags.clickable) {
    if (s.phase === PHASE.SETUP_B || s.phase === PHASE.SETUP_W) suffix = ' (can remove)';
    else if (s.phase === PHASE.SELECT) suffix = ' (can move)';
    else suffix = ' (capture destination)';
  }
  return `${coord}, ${piece}${suffix}`;
}

function renderControls(ui: UI, s: GameState, canUndo: boolean): void {
  if (s.phase === PHASE.MULTI) ui.btnEnd.removeAttribute('hidden');
  else ui.btnEnd.setAttribute('hidden', '');
  ui.btnUndo.disabled = !canUndo;
}

function renderModal(ui: UI, s: GameState): void {
  if (s.phase === PHASE.OVER && s.winner) {
    const loser = s.winner === BLACK ? WHITE : BLACK;
    ui.modalStone.className = '';
    ui.modalStone.classList.add(s.winner);
    ui.modalTitle.textContent = `${cap(s.winner)} Wins!`;
    ui.modalDesc.textContent = `${cap(loser)} has no jumps remaining.`;
    if (ui.modalOverlay.hasAttribute('hidden')) {
      ui.modalOverlay.removeAttribute('hidden');
      const btn = ui.root.querySelector<HTMLButtonElement>('#btn-again');
      btn?.focus();
    }
  } else {
    ui.modalOverlay.setAttribute('hidden', '');
  }
}

export function setAIThinking(ui: UI, on: boolean): void {
  if (on) ui.thinking.removeAttribute('hidden');
  else ui.thinking.setAttribute('hidden', '');
}

export function toggleRulesPanel(ui: UI): void {
  if (ui.rulesPanel.hasAttribute('hidden')) {
    ui.rulesPanel.removeAttribute('hidden');
  } else {
    ui.rulesPanel.setAttribute('hidden', '');
  }
}

/**
 * Animate a transition. Returns a promise that resolves when the animation
 * completes; caller is expected to update state + re-render afterwards.
 */
export async function animateTransition(ui: UI, t: Transition): Promise<void> {
  if (!t.moved && !t.capturedAt) return;

  const tasks: Promise<void>[] = [];

  if (t.capturedAt) {
    const capCell = ui.cellEls[t.capturedAt.r][t.capturedAt.c];
    const capPiece = capCell.querySelector<HTMLElement>('.piece');
    if (capPiece) {
      capPiece.classList.add('capturing');
      tasks.push(waitAnimation(capPiece, 360));
    }
  }

  if (t.moved) {
    const { from, to } = t.moved;
    const srcCell = ui.cellEls[from.r][from.c];
    const dstCell = ui.cellEls[to.r][to.c];
    const srcPiece = srcCell.querySelector<HTMLElement>('.piece');
    if (srcPiece) {
      const srcRect = srcCell.getBoundingClientRect();
      const dstRect = dstCell.getBoundingClientRect();
      const dx = dstRect.left - srcRect.left;
      const dy = dstRect.top - srcRect.top;
      srcPiece.classList.add('moving');
      void srcPiece.offsetWidth;
      srcPiece.style.transform = `translate(${dx}px, ${dy}px)`;
      tasks.push(waitTransition(srcPiece, 320));
    }
  }

  await Promise.all(tasks);
}

function waitTransition(el: HTMLElement, fallbackMs: number): Promise<void> {
  return new Promise<void>((resolve) => {
    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      el.removeEventListener('transitionend', onEnd);
      resolve();
    };
    const onEnd = (ev: TransitionEvent): void => {
      if (ev.target === el && ev.propertyName === 'transform') finish();
    };
    el.addEventListener('transitionend', onEnd);
    window.setTimeout(finish, fallbackMs);
  });
}

function waitAnimation(el: HTMLElement, fallbackMs: number): Promise<void> {
  return new Promise<void>((resolve) => {
    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      el.removeEventListener('animationend', onEnd);
      resolve();
    };
    const onEnd = (ev: AnimationEvent): void => {
      if (ev.target === el) finish();
    };
    el.addEventListener('animationend', onEnd);
    window.setTimeout(finish, fallbackMs);
  });
}

export function flashArrive(ui: UI, r: number, c: number): void {
  const piece = ui.cellEls[r][c].querySelector<HTMLElement>('.piece');
  if (!piece) return;
  piece.classList.remove('arriving');
  void piece.offsetWidth;
  piece.classList.add('arriving');
}
